/**
 * Intake "seen requests" tracker — drives the in-app nudge badge on the
 * Intake sidebar link.
 *
 * We track the set of user-submitted request IDs (`source=user_request`,
 * `status=requested`) the current user has already laid eyes on. The
 * Intake page writes the full current set into localStorage on mount;
 * the Sidebar reads it and renders a count for any new IDs that have
 * shown up since.
 *
 * Why an ID set, not a timestamp:
 *   - Survives clock skew between client and server.
 *   - Cleanly handles the "request was triaged then re-opened" case —
 *     once an ID leaves the requested bucket, it's no longer counted,
 *     and any later return to that bucket would be a genuine new event
 *     worth surfacing.
 *   - No backend change (no created_at field needed on the API).
 *
 * Scope: per-browser. A user signing in on a different device starts
 * fresh. That's fine — Phase 1 auth will pin this server-side later.
 */
const STORAGE_KEY = "rp.intake.seenRequestIds";

export function loadSeenRequestIds(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((x): x is string => typeof x === "string"));
    }
  } catch {
    /* fall through */
  }
  return new Set();
}

export function saveSeenRequestIds(ids: Iterable<string>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(ids)));
    // Other tabs / the sidebar in the same tab need to react when the
    // Intake page marks things seen. localStorage 'storage' events only
    // fire cross-tab, so dispatch a CustomEvent for same-tab listeners.
    window.dispatchEvent(new CustomEvent("rp:intake-seen-changed"));
  } catch {
    /* fall through */
  }
}
